import { useState } from "react"
import { errorToast } from "@/v2-ui/toast"
import Selector from "@/v2-backoffice-client/components/selector"
import type { SelectorTriggerProps } from "@/v2-backoffice-client/components/selector"
import { usePinnedResults } from "@/v2-backoffice-client/components/selector/selector.hooks"
import { useCustomerQuery } from "@/v2-backoffice-client/customer/customer.query"
import TenantCustomerAddManySelectorItem from "@/v2-backoffice-client/tenant/customer/add/many/TenantCustomerAddManySelectorItem"

export type TenantCustomerAddManySelectorProps = SelectorTriggerProps & {
  tenantIds: number[]
  onCompleted?(): void
}

function TenantCustomerAddManySelector(
  props: TenantCustomerAddManySelectorProps,
) {
  const { tenantIds, onCompleted, ...triggerProps } = props

  const [search, setSearch] = useState("")
  const [selectedId, setSelectedId] = useState<string | null>(null)

  const { data, loading, error } = useCustomerQuery({
    skip: search.length < 2,
    variables: {
      input: {
        limit: 10,
        filters:
          search.length >= 2
            ? [{ column: "Name", contains: search }]
            : undefined,
      },
    },
  })

  errorToast(error, {
    title: "Failed to query for customers",
  })

  const customers = data?.getCustomers.items ?? []
  const pinnedCustomers = usePinnedResults({
    results: customers,
    isPinned: (c) => c.ExternalCustomerId === selectedId,
  })

  return (
    <Selector
      onOpenChange={(open) => {
        if (!open) setSelectedId(null)
      }}
    >
      <Selector.Trigger iconSrc="users-alt" {...triggerProps}>
        {triggerProps.children ?? "Update Customer"}
      </Selector.Trigger>

      <Selector.Content>
        <Selector.Search
          placeholder="Search by customer name..."
          onSearch={setSearch}
        />

        <Selector.List
          emptyLabel="No customers found"
          isLoading={loading}
          error={error}
          noResults={customers.length === 0}
          pinned={pinnedCustomers.map((customer) => (
            <TenantCustomerAddManySelectorItem
              key={customer.Id}
              customer={customer}
              tenantIds={tenantIds}
              added
              onSelect={() => {
                setSelectedId(customer.ExternalCustomerId ?? null)
                onCompleted?.()
              }}
            />
          ))}
        >
          {customers.map((customer) => (
            <TenantCustomerAddManySelectorItem
              key={customer.Id}
              customer={customer}
              tenantIds={tenantIds}
              added={customer.ExternalCustomerId === selectedId}
              onSelect={() => {
                setSelectedId(customer.ExternalCustomerId ?? null)
                onCompleted?.()
              }}
            />
          ))}
        </Selector.List>
      </Selector.Content>
    </Selector>
  )
}

export default TenantCustomerAddManySelector
